import React, { useState, useEffect, useRef } from 'react';
import { useFitStore } from '../store/useFitStore';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';

const Messages: React.FC = () => {
  const { user, clients, messages, sendMessage, markMessagesRead } = useFitStore();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const isCoach = user?.role === 'coach';

  // If client, the active chat is always with the coach
  useEffect(() => {
    if (!isCoach && user?.coachId) {
      setActiveChatId(user.coachId);
    }
  }, [isCoach, user]);

  useEffect(() => {
    if (activeChatId) {
      markMessagesRead(activeChatId);
    }
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChatId, messages.length]);

  const filteredMessages = messages.filter(m => 
    (m.senderId === user?.id && m.receiverId === activeChatId) ||
    (m.senderId === activeChatId && m.receiverId === user?.id)
  ).sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));

  const handleSend = async () => {
    if (!inputText || !activeChatId) return;
    await sendMessage(activeChatId, inputText);
    setInputText('');
  };

  const activeClient = clients.find(c => c.id === activeChatId);

  return (
    <div className="main-content" style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: 0, background: 'var(--bg-primary)' }}>
      {isCoach && !activeChatId ? (
        <div style={{ padding: 'var(--spacing-md)' }}>
          <h1 className="text-accent" style={{ marginBottom: 'var(--spacing-lg)', fontSize: 'var(--font-size-2xl)' }}>Messages</h1>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {clients.map(client => {
              const unreadCount = messages.filter(m => m.senderId === client.id && m.receiverId === user?.id && !m.read).length;
              return (
                <div key={client.id} onClick={() => setActiveChatId(client.id)} className="card card-interactive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontWeight: 700 }}>{client.name}</p>
                    <p className="text-muted" style={{ fontSize: '0.8rem' }}>Tap to open chat</p>
                  </div>
                  {unreadCount > 0 && (
                    <div style={{ background: 'var(--status-red)', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800 }}>
                      {unreadCount}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <>
          {/* Blue-ish chat header matching screenshots */}
          <header style={{ padding: '14px 20px', background: 'var(--bg-secondary)', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid var(--glass-border)' }}>
            {isCoach && <button onClick={() => setActiveChatId(null)} className="text-xl leading-none">←</button>}
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-[11px]">👤</div>
              <h2 className="text-base font-semibold tracking-tight m-0">{isCoach ? activeClient?.name : 'Your Coach'}</h2>
            </div>
            <div className="ml-auto text-xl opacity-70">⋯</div>
          </header>

          <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-md)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)', background: 'var(--bg-primary)' }} className="chat-container">
            {filteredMessages.map((msg) => {
              const isMe = msg.senderId === user?.id;
              // Demo rich program share card (matches Image 1/3)
              const isProgramShare = msg.text && msg.text.includes('Summer Shred');

              if (isProgramShare) {
                return (
                  <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                    <div className="message-bubble coach">
                      Hey Steph, here's my newest training program! You can try it out if you want!
                    </div>
                    {/* Embedded program card */}
                    <div className="program-share-card">
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '42%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2rem' }}>🏋️</div>
                        <div className="content" style={{ flex: 1 }}>
                          <div className="info">
                            <h4>Summer Shred Sneak Peek</h4>
                            <p>1 week free program</p>
                            <p style={{ marginTop: 4, fontSize: '0.7rem', color: 'var(--text-muted)' }}>Duration: 1 week</p>
                          </div>
                          <div className="badge" style={{ background: 'var(--status-green)', color: 'white', padding: '1px 7px', fontSize: '10px', borderRadius: 999, alignSelf: 'flex-start' }}>Free</div>
                        </div>
                      </div>
                      <div className="reactions">
                        <span className="reaction-pill">👍 1</span>
                        <span className="reaction-pill">🔥 1</span>
                        <span className="reaction-pill">😊</span>
                      </div>
                    </div>
                    <div className="message-meta" style={{ justifyContent: isMe ? 'flex-end' : 'flex-start' }}>10:53</div>
                  </div>
                );
              }

              return (
                <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                  <div className={`message-bubble ${isMe ? 'coach' : 'client'}`}>
                    {msg.text}
                  </div>
                  <div className="message-meta" style={{ justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                    {isMe ? 'You' : (activeClient?.name || 'Coach')} • 10:53
                  </div>
                </div>
              );
            })}
            <div ref={scrollRef} />
          </div>

          {/* Input bar matching screenshot style */}
          <div style={{ padding: 'var(--spacing-sm) var(--spacing-md)', background: 'var(--bg-primary)', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8, fontSize: '1.1rem', color: 'var(--text-muted)' }}>📷 📎 🖼️</div>
            <Input 
              value={inputText} 
              onChange={(e) => setInputText(e.target.value)} 
              placeholder="Type a message..." 
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              style={{ flex: 1, background: 'var(--bg-secondary)' }}
            />
            <Button onClick={handleSend} size="sm">Send</Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Messages;
